import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'

/** Texto libre, sin lista cerrada: a un auditor le sirve el criterio profesional en sus palabras. */
function MotivoField({
  value,
  onChange,
  testId,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  testId: string
  placeholder: string
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-1.5">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('ipevr.descarte.motivoLabel')}
      </p>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={1000}
        data-testid={testId}
      />
    </div>
  )
}

export function DescartarFilaDialog({
  open,
  onOpenChange,
  sugerido,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sugerido: boolean
  pending: boolean
  error: string | null
  onConfirm: (motivo: string) => void
}) {
  const {t} = useTranslation()
  const [motivo, setMotivo] = useState('')

  useEffect(() => {
    if (open) setMotivo('')
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ipevr-descartar-fila-dialog">
        <DialogHeader>
          <DialogTitle>
            {sugerido ? t('ipevr.descarte.fila.titleSugerido') : t('ipevr.descarte.fila.titleConfirmada')}
          </DialogTitle>
          <DialogDescription>{t('ipevr.descarte.fila.body')}</DialogDescription>
        </DialogHeader>
        <MotivoField
          value={motivo}
          onChange={setMotivo}
          testId="descartar-fila-motivo"
          placeholder={t('ipevr.descarte.fila.motivoPlaceholder')}
        />
        {error && (
          <p className="text-xs text-destructive" data-testid="descartar-fila-error">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={pending || motivo.trim().length === 0}
            onClick={() => onConfirm(motivo.trim())}
            data-testid="descartar-fila-confirmar">
            {sugerido ? t('ipevr.grid.sugerido.rechazarConfirmAction') : t('ipevr.grid.deleteFila')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Si es la última procedencia, ofrece descartar o conservar la fila y espera: Kairos no elige. */
export function DesasociarProcedenciaDialog({
  open,
  onOpenChange,
  personaName,
  esUltima,
  pending,
  error,
  onDesasociar,
  onDescartarFila,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  personaName: string
  esUltima: boolean
  pending: boolean
  error: string | null
  onDesasociar: (motivo: string) => void
  onDescartarFila: (motivo: string) => void
}) {
  const {t} = useTranslation()
  const [motivo, setMotivo] = useState('')

  useEffect(() => {
    if (open) setMotivo('')
  }, [open])

  const sinMotivo = pending || motivo.trim().length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ipevr-desasociar-procedencia-dialog">
        <DialogHeader>
          <DialogTitle>{t('ipevr.descarte.procedencia.title', {name: personaName})}</DialogTitle>
          <DialogDescription>{t('ipevr.descarte.procedencia.body')}</DialogDescription>
        </DialogHeader>

        {esUltima && (
          <div className="notice-amber rounded-lg px-3.5 py-2.5 text-xs" data-testid="desasociar-ultima-aviso">
            {t('ipevr.descarte.procedencia.ultimaAviso')}
          </div>
        )}

        <MotivoField
          value={motivo}
          onChange={setMotivo}
          testId="desasociar-motivo"
          placeholder={t('ipevr.descarte.procedencia.motivoPlaceholder')}
        />
        {error && (
          <p className="text-xs text-destructive" data-testid="desasociar-error">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          {esUltima && (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={sinMotivo}
              onClick={() => onDescartarFila(motivo.trim())}
              data-testid="desasociar-descartar-fila">
              {t('ipevr.descarte.procedencia.descartarFila')}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            disabled={sinMotivo}
            onClick={() => onDesasociar(motivo.trim())}
            data-testid="desasociar-confirmar">
            {esUltima ? t('ipevr.descarte.procedencia.conservarFila') : t('ipevr.descarte.procedencia.desasociar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
